import { useState } from 'react';
import { sortRows } from './sortRows.mjs';

/**
 * Reusable sort hook for Settings tables.
 * Usage:
 *   const { sorted, Th } = useSortable(data, 'name');
 *   <thead><tr><Th col="name" label="Name" /><Th col="date" label="Created" /></tr></thead>
 *   <tbody>{sorted.map(row => ...)}</tbody>
 */
// sortKeyMap (optional): { [columnKey]: rowKeyToSortBy } — lets a column DISPLAY one value
// (e.g. a formatted date string) while SORTING by another (e.g. a raw ISO/epoch field), so a
// formatted date column sorts chronologically instead of lexicographically. Backward-compatible:
// callers that omit it sort by the column key as before.
export default function useSortable(data, defaultKey = '', sortKeyMap = {}, defaultDir = 'asc') {
  const [key, setKey] = useState(defaultKey);
  const [dir, setDir] = useState(defaultDir);

  function toggle(col) {
    if (key === col) setDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setKey(col); setDir('asc'); }
  }

  const sorted = sortRows(data, key, dir, sortKeyMap);

  // `title` (optional, 894b): a tooltip on the header. Purely additive — every existing caller omits it
  // and renders exactly as before. Sales ▸ Orders needs it because three of its money headers carry the
  // explanation of WHICH basis the column is (order value vs invoiced vs gross AR), and that text must
  // survive the column becoming sortable.
  function Th({ col, label, style = {}, align = 'left', title }) {
    const active = key === col;
    return (
      <th
        onClick={() => toggle(col)}
        title={title}
        style={{
          cursor: 'pointer',
          // 766: selectable header text (was userSelect:'none'). This hook styles EVERY sortable <th> in
          // the app — General Journal, Settings tables, etc. — so this one line is most of the fix.

          padding: '10px',
          textAlign: align,
          ...style,
        }}
      >
        {label}{' '}
        <span style={{ opacity: active ? 1 : 0.35, fontSize: 10 }}>
          {active ? (dir === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      </th>
    );
  }

  // Additive (fb-89): expose the current sort key/dir and an imperative setter so a caller can drive
  // the sort from an external control (e.g. a "Sort by order" checkbox) and reflect column clicks.
  // Every existing caller ignores the extra return fields, so behaviour is unchanged for them.
  return { sorted, Th, sortKey: key, sortDir: dir, sortBy: (col, d = 'asc') => { setKey(col); setDir(d); } };
}
